import { toArray } from "lodash";
import PropTypes from "prop-types";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes, matchPath, useLocation } from "react-router";

import { GlobalContext } from "#/context";

/**
 * React router dom Switch wrapper to store defined routes with path and name to context.
 *
 * This enables us to define route inside a switch without use of separate code-list of route properties.
 *
 * @param props
 * @returns {*}
 * @constructor
 */
export default function RouterSwitch({ children }) {
  const [globalContext, setGlobalContext] = useContext(GlobalContext);
  const { pathname } = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const parseRoute = (routes, item, parentPath) => {
      if (
        item &&
        item.type === Route &&
        item.props.path &&
        item.props.path !== "*"
      ) {
        const basePath = parentPath === "/" ? "" : parentPath;
        const fullPath = parentPath
          ? `${basePath}/${item.props.path}`
          : item.props.path;
        routes.push({
          path: item.props.path,
          fullPath: fullPath,
          name: item.props.name,
          rootOnlyBreadcrumb: !!item.props.rootOnlyBreadcrumb,
          end: item.props.end === true,
          resolver: item.props.resolver,
          children: toArray(item.props.children).reduce(
            (childRoutes, child) => parseRoute(childRoutes, child, fullPath),
            []
          ),
        });
      }
      return routes;
    };

    const traceBreadcrumb = (routes, breadcrumbs) => {
      const item = routes.find(
        (route) => !!matchPath({ path: route.fullPath, end: false }, pathname)
      );
      if (item) {
        if (!item.rootOnlyBreadcrumb || item.fullPath === pathname) {
          breadcrumbs.push(item);
        }
        traceBreadcrumb(item.children, breadcrumbs);
      }
      return breadcrumbs;
    };

    const routes = children.reduce(
      (routes, item) => parseRoute(routes, item, undefined),
      []
    );
    const breadcrumbs = traceBreadcrumb(routes, []);

    setGlobalContext((previousState) => ({
      ...previousState,
      routes: routes,
      breadcrumbs: breadcrumbs,
    }));
  }, [pathname, t]);

  return <Routes>{children}</Routes>;
}

RouterSwitch.propTypes = {
  children: PropTypes.node,
};
